import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import TripStoryCard from "../components/app/TripStoryCard";
import NewStoryModal from "../components/app/NewStoryModal";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Image as ImageIcon, Loader2, Filter } from "lucide-react";
import ShareMenu from "../components/app/ShareMenu";

export default function Wall() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("all"); // all | mine
  const [total, setTotal] = useState(0);

  const load = async (which = filter) => {
    setLoading(true);
    try {
      const q = {};
      if (which === "mine" && user) q.user_id = user.user_id;
      const { data } = await api.get("/wall", { params: q });
      setStories(data?.items || []);
      setTotal(data?.total || 0);
    } catch {
      // Graceful failure — keep whatever stories we already had (or empty list)
      // so a flaky /wall fetch can't crash the page or leave it stuck on Loading.
      setStories((prev) => prev || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  // If ?new=1 in URL, auto-open the modal (used by share-this-trip CTAs)
  useEffect(() => {
    if (params.get("new") === "1") {
      if (!user) { nav("/login?next=/wall%3Fnew%3D1"); return; }
      setShowNew(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [params, user]);

  const onCreated = (story) => {
    setStories((prev) => [story, ...prev]);
    setTotal((t) => t + 1);
  };

  const onChange = (updated) => {
    setStories((prev) => prev.map((s) => (s.story_id === updated.story_id ? updated : s)));
  };

  const openNew = () => {
    if (!user) { nav("/login?next=/wall%3Fnew%3D1"); return; }
    setShowNew(true);
  };

  const filtered = useMemo(() => stories, [stories]);

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="wall-page">
      <Header active="wall" />

      {/* HERO STRIP */}
      <section className="relative overflow-hidden border-b hairline-strong bg-gradient-to-br from-[#ff4500] via-[#e03d00] to-[#a82e00] text-white">
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-16 grid lg:grid-cols-[1.6fr_1fr] gap-8 items-end">
          <div>
            <span className="eyebrow !text-white/80">{t("wall.eyebrow")}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-3">
              {t("wall.h1")}
            </h1>
            <p className="mt-4 text-base lg:text-lg max-w-xl leading-snug text-white/90">
              {t("wall.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <button
                onClick={openNew}
                className="bg-white text-[#ff4500] font-bold uppercase tracking-wider text-sm px-5 py-3 inline-flex items-center gap-2 shadow-[0_4px_0_0_#111] hover:shadow-[0_2px_0_0_#111] hover:translate-y-[2px] transition-all"
                data-testid="wall-new-cta"
              >
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                {t("wall.shareTrip")}
              </button>
              <Link
                to="/browse"
                className="border-2 border-white text-white font-bold uppercase tracking-wider text-sm px-5 py-3 inline-flex items-center gap-2 hover:bg-white hover:text-[#ff4500] transition-colors"
                data-testid="wall-browse-cta"
              >
                {t("wall.findCampsite")}
              </Link>
              <ShareMenu
                url={`${process.env.REACT_APP_BACKEND_URL}/api/share/wall`}
                title="The Trip Wall · CampCompass UK"
                text="Real photos, real trips from UK campers. Find your next favourite pitch on CampCompass UK."
                buttonClassName="border-2 border-white/40 text-white text-sm px-4 py-3 inline-flex items-center gap-2 uppercase tracking-wider font-bold hover:bg-white/10 transition-colors"
                buttonLabel={t("wall.shareWall")}
                testId="wall-share-hero"
              />
            </div>
          </div>
          <div className="hidden lg:block text-right">
            <div className="font-display text-7xl leading-none">{total.toLocaleString()}</div>
            <div className="eyebrow !text-white/70 mt-2">{t("wall.tripsShared")}</div>
          </div>
        </div>
      </section>

      {/* CONTROLS */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-xs mono uppercase tracking-wider">
          <Filter className="w-3.5 h-3.5 text-[#888]" strokeWidth={1.75} />
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 border hairline ${filter === "all" ? "bg-black text-white border-black" : "hover:bg-[#f5f5f5]"}`}
            data-testid="wall-filter-all"
          >
            {t("wall.allTrips")}
          </button>
          {user && (
            <button
              onClick={() => setFilter("mine")}
              className={`px-3 py-1.5 border hairline ${filter === "mine" ? "bg-black text-white border-black" : "hover:bg-[#f5f5f5]"}`}
              data-testid="wall-filter-mine"
            >
              {t("wall.myTrips")}
            </button>
          )}
        </div>
        <button
          onClick={openNew}
          className="btn-primary text-xs px-4 py-2.5 inline-flex items-center gap-2"
          data-testid="wall-new-cta-secondary"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("wall.shareTrip")}
        </button>
      </div>

      {/* GRID */}
      <main className="flex-1 max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 w-full">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#888] gap-2 text-sm mono" data-testid="wall-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("wall.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto" data-testid="wall-empty">
            <ImageIcon className="w-12 h-12 text-[#ff4500]/40 mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl mb-2">
              {filter === "mine" ? t("wall.emptyMineTitle") : t("wall.emptyAllTitle")}
            </h2>
            <p className="text-sm text-[#666] leading-snug mb-5">
              {filter === "mine" ? t("wall.emptyMineBody") : t("wall.emptyAllBody")}
            </p>
            <button onClick={openNew} className="btn-primary text-xs px-5 py-2.5" data-testid="wall-empty-cta">
              <Plus className="w-3.5 h-3.5 inline mr-1" strokeWidth={2.5} /> {t("wall.postTrip")}
            </button>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="wall-grid">
            {/* iter194 — water-tight anon gate: only the first 3 trip
                stories render for guests, followed by a full-width lock
                CTA. Signed-in members see everything. */}
            {(user ? filtered : filtered.slice(0, 3)).map((s) => (
              <TripStoryCard key={s.story_id} story={s} onChange={onChange} />
            ))}
          </div>
        )}
        {!user && filtered.length > 3 && !loading && (
          <div
            className="mt-10 max-w-2xl mx-auto border-2 border-[#0e0e0e] bg-white p-6 sm:p-8 text-center shadow-[0_6px_0_0_#0e0e0e]"
            data-testid="wall-anon-lock"
          >
            <div className="inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest bg-[#0e0e0e] text-white px-2 py-0.5 mb-3">
              <ImageIcon className="w-3 h-3" /> +{filtered.length - 3} more trips locked
            </div>
            <h3 className="font-display text-2xl leading-tight mb-2">
              Sign up free to see the whole wall
            </h3>
            <p className="text-sm text-[#555] leading-snug mb-4">
              Real photos from {total.toLocaleString()} UK camping trips, kudos, full-size gallery, and the ability to post your own — all free while we grow.
            </p>
            <Link
              to={`/register?next=${encodeURIComponent("/wall")}`}
              className="inline-flex items-center gap-2 bg-[#0a7f3f] text-white font-black text-xs uppercase tracking-widest px-5 py-3 shadow-[0_4px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#111] transition-transform"
              data-testid="wall-anon-lock-signup"
            >
              Sign up free
            </Link>
          </div>
        )}
      </main>

      <NewStoryModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={onCreated}
      />

      <Footer />
    </div>
  );
}
